/**
 * PhaseRoadmap — circuito cinematográfico das fases do programa.
 *
 * Props:
 *   phases: [{id, code, name_pt, tagline_pt, color, status: 'passed'|'active'|'locked'}]
 *   onPhaseClick: optional callback (phaseId)
 */

import React from "react";
import { motion } from "framer-motion";
import { Lock, Check, Sparkles } from "lucide-react";

const STATUS_META = {
  passed: { Icon: Check, label: "Concluída", textColor: "text-emerald-400" },
  active: { Icon: Sparkles, label: "Em curso", textColor: "text-zinc-100" },
  locked: { Icon: Lock, label: "Bloqueada", textColor: "text-zinc-500" },
};

export default function PhaseRoadmap({ phases = [], onPhaseClick }) {
  return (
    <div className="relative w-full" data-testid="phase-roadmap">
      {/* Connector line behind nodes */}
      <div className="hidden md:block absolute top-12 left-12 right-12 h-px bg-gradient-to-r from-zinc-800 via-zinc-700 to-zinc-800 z-0" />

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 relative z-10">
        {phases.map((p, i) => {
          const meta = STATUS_META[p.status] || STATUS_META.locked;
          const Icon = meta.Icon;
          const isActive = p.status === "active";
          const isPassed = p.status === "passed";
          const isLocked = p.status === "locked";

          return (
            <motion.button
              key={p.id}
              onClick={() => onPhaseClick?.(p.id)}
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
              whileHover={{ y: -3 }}
              className="relative p-4 rounded-2xl border bg-zinc-950 text-left cursor-pointer transition-all"
              style={{
                borderColor: isActive ? p.color : isPassed ? `${p.color}55` : "#27272a",
                boxShadow: isActive ? `0 0 30px ${p.color}40` : "none",
                opacity: isLocked ? 0.55 : 1,
              }}
              data-testid={`phase-node-${p.id}`}
            >
              {/* Pulse halo if active */}
              {isActive && (
                <motion.div
                  className="absolute inset-0 rounded-2xl pointer-events-none"
                  style={{ background: `radial-gradient(circle at 30% 30%, ${p.color}20, transparent 70%)` }}
                  animate={{ opacity: [0.4, 0.8, 0.4] }}
                  transition={{ duration: 3, repeat: Infinity }}
                />
              )}

              {/* Status icon */}
              <div
                className="w-9 h-9 rounded-full flex items-center justify-center mb-3 relative z-10"
                style={{
                  background: isActive ? `${p.color}25` : isPassed ? `${p.color}20` : "#18181b",
                  boxShadow: isActive ? `0 0 20px ${p.color}50` : "none",
                  border: `1.5px solid ${isLocked ? "#3f3f46" : p.color}`,
                }}
              >
                <Icon className="w-4 h-4" style={{ color: isLocked ? "#71717a" : p.color }} />
              </div>

              <p className="text-[10px] uppercase tracking-widest mb-1 relative z-10" style={{ color: isLocked ? "#71717a" : p.color }}>
                Bloco {p.id} · {meta.label}
              </p>
              <h3 className={`text-base font-bold mb-1 relative z-10 ${meta.textColor}`}>
                {p.name_pt}
              </h3>
              <p className="text-[11px] text-zinc-400 leading-snug relative z-10">{p.tagline_pt}</p>
              {p.weeks && (
                <p className="text-[10px] text-zinc-600 mt-2 relative z-10">
                  Sem {p.weeks[0]}–{p.weeks[p.weeks.length - 1]} · {p.duration_days}d
                </p>
              )}
            </motion.button>
          );
        })}
      </div>
    </div>
  );
}
